/**
 * Rules for the "tick all subtasks" checkbox (work item Summary and the
 * Work page's expanded subtask list).
 *
 * Ticking all sets every subtask and the work item to Completed. Unticking
 * all should put the statuses back the way they were (Roadmap 1787), so the
 * statuses from just before "tick all" are remembered in this browser.
 */

export type StatusRef = {
  statusId: string | null;
  subStatusId: string | null;
};

export type AllDoneSnapshot = {
  work: StatusRef;
  tasks: Record<string, StatusRef>;
};

type TaskStatus = {
  id: string;
  statusId: string | null;
  subStatusId: string | null;
};

export type UntickPlan = {
  /** Subtasks to set back (only those still Completed). */
  tasks: { id: string; statusId: string | null; subStatusId: string | null }[];
  /** The work item's earlier status, or null to leave it as it is. */
  work: StatusRef | null;
};

const STORAGE_PREFIX = "proc_subtasks_all_done:";

export const snapshotKey = (workId: string) => `${STORAGE_PREFIX}${workId}`;

export const buildSnapshot = (
  work: StatusRef,
  tasks: TaskStatus[],
): AllDoneSnapshot => ({
  work: {
    statusId: work.statusId || null,
    subStatusId: work.subStatusId || null,
  },
  tasks: Object.fromEntries(
    tasks.map((task) => [
      task.id,
      {
        statusId: task.statusId || null,
        subStatusId: task.subStatusId || null,
      },
    ]),
  ),
});

export const parseSnapshot = (
  raw: string | null | undefined,
): AllDoneSnapshot | null => {
  if (!raw) return null;

  try {
    const value = JSON.parse(raw);

    if (
      !value ||
      typeof value !== "object" ||
      typeof value.work !== "object" ||
      typeof value.tasks !== "object"
    ) {
      return null;
    }

    return value as AllDoneSnapshot;
  } catch {
    return null;
  }
};

export const readSnapshot = (workId: string): AllDoneSnapshot | null => {
  try {
    return parseSnapshot(window.localStorage.getItem(snapshotKey(workId)));
  } catch {
    return null;
  }
};

export const saveSnapshot = (workId: string, snapshot: AllDoneSnapshot) => {
  try {
    window.localStorage.setItem(snapshotKey(workId), JSON.stringify(snapshot));
  } catch {
    // Storage blocked: unticking then simply leaves the statuses as they are.
  }
};

export const clearSnapshot = (workId: string) => {
  try {
    window.localStorage.removeItem(snapshotKey(workId));
  } catch {
    // ignore
  }
};

/**
 * What to set back when every subtask is unticked.
 * - Subtasks: only those that are still Completed and were something else
 *   before "tick all" (a status changed since then is left alone).
 * - Work item: only while it is still Completed. The remembered status
 *   comes first; without it, the server's previous status (saved when all
 *   subtasks were marked done) is used.
 */
export const planUntickAll = ({
  snapshot,
  tasks,
  completedStatusId,
  work,
  serverPreviousStatusId,
}: {
  snapshot: AllDoneSnapshot | null;
  tasks: TaskStatus[];
  completedStatusId: string | null | undefined;
  work: StatusRef;
  serverPreviousStatusId?: string | null;
}): UntickPlan => {
  if (!completedStatusId) {
    return { tasks: [], work: null };
  }

  const taskRestores = tasks.flatMap((task) => {
    const before = snapshot?.tasks?.[task.id];

    if (
      task.statusId !== completedStatusId ||
      !before ||
      before.statusId === completedStatusId
    ) {
      return [];
    }

    return [
      {
        id: task.id,
        statusId: before.statusId || null,
        subStatusId: before.subStatusId || null,
      },
    ];
  });

  let workRestore: StatusRef | null = null;

  if (work.statusId === completedStatusId) {
    if (
      snapshot?.work?.statusId &&
      snapshot.work.statusId !== completedStatusId
    ) {
      workRestore = {
        statusId: snapshot.work.statusId,
        subStatusId: snapshot.work.subStatusId || null,
      };
    } else if (
      !snapshot &&
      serverPreviousStatusId &&
      serverPreviousStatusId !== completedStatusId
    ) {
      workRestore = { statusId: serverPreviousStatusId, subStatusId: null };
    }
  }

  return { tasks: taskRestores, work: workRestore };
};
